import { useState } from 'react';

import { faChevronRight, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDroppable } from '@dnd-kit/core';
import clsx from 'clsx';
import { Button } from 'ui-kit';

import { LoanField } from '__generated__/globalTypes';
import { FieldIdAndLabel, FieldsMap } from './MapRequiredFieldsCard';
import { SearchBox } from './SearchBox';

const fieldItemWidthInPixels = 288;

// Allow precisely two items to show side-by-side.
const fieldGroupContainerWidthInPixels = fieldItemWidthInPixels * 2;

const fieldsListWrapperClass = 'min-w-588px text-xs text-gray-300';

const fieldListClass = 'h-540px overflow-y-auto';

const listItemClass =
  'flex min-h-60px w-72 items-center justify-between py-0 pl-22px pr-6 text-sm leading-5 text-slate-50 hover:bg-slate-600';
const listItemOverClass = 'rounded border border-dashed bg-accent-muted';

const undoBtnClass = 'border border-slate-200 text-slate-200 bg-transparent hover:bg-transparent';

const fieldGroupContainerClass = 'flex w-576px flex-wrap';

type HeaderItem = {
  label: string;
};

type UnmappedFieldListItemProps = {
  field: FieldIdAndLabel;
  disabled?: boolean;
};

const UnmappedFieldListItem = ({
  field,
  disabled = false,
}: Omit<UnmappedFieldListItemProps, 'onHeaderDrop'>) => {
  const { isOver, setNodeRef } = useDroppable({
    id: field.fieldId,
    disabled,
    data: { fieldId: field.fieldId },
  });

  return (
    <div
      ref={setNodeRef}
      data-testid={`unmapped-field-row-${field.fieldId}`}
      className={clsx(listItemClass, isOver && !disabled && listItemOverClass)}
    >
      <div>
        <div>{field.label}</div>
      </div>
    </div>
  );
};

type MappedFieldListItemProps = {
  field: FieldIdAndLabel;
  onUnmap: (fieldId: LoanField) => void;
  mappedHeader?: string;
  disabled?: boolean;
};

const MappedFieldListItem = ({
  field,
  onUnmap,
  mappedHeader,
  disabled = false,
}: MappedFieldListItemProps) => {
  return (
    <div className={listItemClass} data-testid={`mapped-field-row-${field.fieldId}`}>
      <div>
        <div>{field.label}</div>
        <div className="text-slate-400">{mappedHeader}</div>
      </div>
      <Button
        label="undo"
        size="small"
        type="secondary"
        className={undoBtnClass}
        disabled={disabled}
        onClick={() => onUnmap(field.fieldId)}
        data-testid={`mapped-field-undo-${field.fieldId}`}
      >
        UNDO
      </Button>
    </div>
  );
};

const fieldGroupHeaderClass =
  'flex items-center justify-between py-5px pl-5 pr-6 text-xs leading-5 text-gray-500 bg-background-surface hover:bg-slate-600';

type FieldGroupProps = {
  fields: FieldIdAndLabel[];
  map: FieldsMap;
  searchTerm?: string;
  onUnmap: (fieldId: LoanField) => void;
  header: string;
  disabled?: boolean;
};

const FieldGroup = ({
  fields,
  map,
  searchTerm,
  onUnmap,
  header,
  disabled = false,
}: FieldGroupProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const matchesSearchText = (field: FieldIdAndLabel) => {
    if (!searchTerm) return true;
    return field.label.toLowerCase().includes(searchTerm?.toLowerCase());
  };

  const matchingFields = fields
    .filter(matchesSearchText)
    .sort((a, b) => a.label.localeCompare(b.label));
  const unmappedFields = matchingFields.filter((field) => !map[field.fieldId]);
  const mappedFields = matchingFields.filter((field) => map[field.fieldId]);

  const toggleIsOpen = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <div className={fieldGroupHeaderClass} onClick={toggleIsOpen}>
        {header}
        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronRight} />
      </div>
      {isOpen && (
        <div className={fieldGroupContainerClass}>
          {mappedFields.map((field) => {
            return (
              <MappedFieldListItem
                key={field.fieldId}
                field={field}
                mappedHeader={map[field.fieldId]}
                onUnmap={onUnmap}
                disabled={disabled}
              />
            );
          })}
          {unmappedFields.map((field) => {
            return <UnmappedFieldListItem key={field.fieldId} field={field} disabled={disabled} />;
          })}
        </div>
      )}
    </>
  );
};

const FieldsList = ({
  requiredFields,
  optionalFields,
  map,
  onUnmap,
  disabled = false,
}: {
  requiredFields: FieldIdAndLabel[];
  optionalFields: FieldIdAndLabel[];
  map: FieldsMap;
  onUnmap: (fieldId: LoanField) => void;
  disabled?: boolean;
}) => {
  const [searchTerm, setSearchTerm] = useState<string>();

  const handleSearchTermChanged = (text: string) => {
    setSearchTerm(text);
  };

  return (
    <div className={fieldsListWrapperClass}>
      <p className="mb-1">Search for an ORSNN column to map to</p>
      <SearchBox
        placeholder="Filter fields by name"
        onSearchTermChanged={handleSearchTermChanged}
      />
      <div className={clsx(fieldListClass, 'py-2')}>
        <FieldGroup
          header="REQUIRED"
          fields={requiredFields}
          map={map}
          searchTerm={searchTerm}
          onUnmap={onUnmap}
          disabled={disabled}
        />
        <FieldGroup
          header="OPTIONAL"
          fields={optionalFields}
          map={map}
          searchTerm={searchTerm}
          onUnmap={onUnmap}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export default FieldsList;
